/**
 * Verifisert oversikt over hvilke rapporttyper som faktisk kan berikes med data
 * ved utskrift, og hva de kan berikes med.
 *
 * KILDE (verifisert, ikke avledet fra kommentarer):
 *  - BizClick.WMS.Core/Services/PrintingService.cs  — hvilken LabelContext hver Print*-metode bygger
 *  - BizClick.WMS.Data/Repositories/LabelDataResolver.cs — hvilke tabeller konteksten slår opp
 *  - BizClick.WMS.Core/Services/Cargonizer/TaProviders/TaLabelPrinter.cs — TA-fraktetikett (logges som 99)
 *
 * VIKTIG: Dette er en frontend "advisory"-konstant. Den må holdes i synk manuelt
 * hvis PrintingService endrer hvilken kontekst en rapporttype bygger. Se HTML-dok
 * `public/rapporttyper-labelcontext-oversikt.html` for full begrunnelse.
 */

export type EnrichmentKind =
  | 'db'            // Beriket fra database via LabelContext
  | 'override-only' // Tom kontekst — kun verdier avsender sender som override + firma/system
  | 'document';     // (historisk — dokumenttypene 10-14 rendres nå fra LayoutJson og er 'db')

export interface EnrichmentInfo {
  kind: EnrichmentKind;
  /** Tabeller som fylles automatisk fra DB for denne typen (utover firma/system). */
  dbSources: string[];
  /** Kort forklaring (norsk). */
  note: string;
}

/**
 * Firma-/systemverdier fylles ALLTID (SystemSetup slås opp betingelsesløst,
 * pluss statiske verdier som dato/klokke/operatør). Gjelder alle typer.
 */
export const ALWAYS_AVAILABLE_LABEL = 'Firma/system (alltid)';

/** System-plassholdere som alltid fylles automatisk, uansett rapporttype. */
const SYSTEM_KEYS = new Set<string>([
  'CompanyName', 'CompanyAddress', 'CompanyOrgNo',
  'CurrentDate', 'CurrentTime', 'OperatorName',
]);

/**
 * Enkle plassholder-nøkler → kilde-tabell (speiler SystemAliases i LabelDataResolver).
 * Brukes til å avgjøre om en plassholder fylles fra DB for en gitt type.
 */
const KEY_SOURCE_TABLE: Record<string, string> = {
  // Vare
  ItemNo: 'Items', ItemName: 'Items', ItemDescription: 'Items', ItemUnitCode: 'Items',
  // Lager
  BinCode: 'StcBin', LocationName: 'StcBin', ZoneName: 'StcZone', WarehouseName: 'Warehouse',
  // Ordre (salg)
  OrderNo: 'Orders', CustomerNo: 'Orders', CustomerName: 'Orders',
  CustomerReference: 'Orders', DeliveryAddress: 'Orders',
  LineNo: 'OrderLine', SerialNo: 'OrderLine', Quantity: 'OrderLine',
  // Batch/serie
  BatchNo: 'OrderBatchNo', ExpiryDate: 'OrderBatchNo', BestBeforeDate: 'OrderBatchNo',
  // SSCC/pall
  SSCC: 'OrderSSCC', GTIN: 'OrderSSCC', PalletNo: 'PListSSCC',
};

const DB: EnrichmentKind = 'db';
const OVERRIDE: EnrichmentKind = 'override-only';

/**
 * Vareetikettene (1–3) sender lageret operatøren står på (TW 36430602). Da utleder
 * resolveren varens lokasjon på lageret (ItemBins, standardlokasjon, flest varer) og
 * henter StcBin/StcZone. «Alle lager» = ingen lagerdata. {{Items.StdLocationName}} er uendret.
 */
const ITEM_WAREHOUSE_SOURCES = ['Warehouse', 'ItemBins', 'StcBin', 'StcZone'];
const ITEM_WAREHOUSE_NOTE = 'Med valgt lager også varens lokasjon på lageret ({{BinCode}}, {{LocationName}}, {{StcBin.*}}); tomt ved «Alle lager».';

/** Verifisert mapping per rapporttype-verdi (jf. Reports.ReportType). */
export const REPORT_TYPE_ENRICHMENT: Record<number, EnrichmentInfo> = {
  1:  { kind: DB, dbSources: ['Items', ...ITEM_WAREHOUSE_SOURCES], note: `Vareetikett — vare-master. ${ITEM_WAREHOUSE_NOTE}` },
  2:  { kind: DB, dbSources: ['Items', 'POrders', 'POrderLine', ...ITEM_WAREHOUSE_SOURCES], note: `Vare + innkjøpsordre (linje). ${ITEM_WAREHOUSE_NOTE}` },
  3:  { kind: DB, dbSources: ['Items', 'Orders', 'OrderLine', ...ITEM_WAREHOUSE_SOURCES], note: `Vare + salgsordre. Kun ordrens FØRSTE linje; ikke batch/SSCC. ${ITEM_WAREHOUSE_NOTE}` },
  4:  { kind: DB, dbSources: ['StcBin', 'StcZone', 'Warehouse'], note: 'Lokasjon. Krever WarehouseId i kallet; ellers kun override.' },
  // 5 (LocationLabelAlt) fjernet 2026-07-15 — død type uten data/utskriftsflyt.
  // Dokumenttyper (10–14): rendres fra LayoutJson i dokumentmodus (2026-07).
  // Linjetabellen (Table-elementets DataSource) fylles fra utskriftskallet —
  // unntatt PListLines som hentes fra databasen (plukklistens faktiske linjer).
  // PList-kontekst kjedes til første ordre (PListOrders → Orders, + Shipment når
  // pakkemodulen har koblet en), så ordrehodet fylles fra DB (2026-10).
  10: { kind: DB, dbSources: ['PList', 'PListOrders', 'Orders', 'OrderLine', 'Shipment', 'PListSSCC', 'PackingSlipLines (rader fra utskriften)', 'PListLines (rader fra DB)', 'ShipmentLines (rader fra DB)'],
        note: 'Pakkseddel (dokumentmodus). Header/footer: PList, første ordre (kunde, leveringsadresse, referanser), Shipment (kolli/vekt) fra DB; PListId, dato, totaler fra utskriftskallet. Linjetabell: PackingSlipLines (fra kallet), PListLines (fra DB) eller ShipmentLines (alle ordrelinjer i forsendelsen, grupperbar per ordre). Ved direkte plukkavslutning uten forsendelse skrives én pakkseddel per mottaker: hode og ShipmentLines begrenses da til mottakerens ordre.' },
  11: { kind: DB, dbSources: ['PList', 'PListOrders', 'Orders', 'OrderLine', 'Shipment', 'PListSSCC', 'PackingSlipLines (rader fra utskriften)', 'PListLines (rader fra DB)', 'ShipmentLines (rader fra DB)'],
        note: 'Pakkseddel detaljert (dokumentmodus) — som type 10.' },
  12: { kind: DB, dbSources: ['PList', 'PListOrders', 'Orders', 'OrderLine', 'Shipment', 'PListSSCC', 'PListLines (rader fra DB)', 'ShipmentLines (rader fra DB)'],
        note: 'Plukkliste (dokumentmodus). Linjetabellen hentes fra databasen (PListLine + OrderLine-beskrivelse) via PList-ID; ordrehodet fra første ordre på listen.' },
  13: { kind: DB, dbSources: ['CountLines (rader fra utskriften)'],
        note: 'Telleliste (dokumentmodus). Lager/sone/blindtelling + linjer kommer fra utskriftskallet.' },
  14: { kind: DB, dbSources: ['POrders', 'PutawayLines (rader fra utskriften)'],
        note: 'Innlagringsplan (dokumentmodus). Innkjøpsordre-kontekst; fra/til-linjer fra utskriftskallet.' },
  40: { kind: DB, dbSources: ['Orders', 'OrderLine', 'OrderBatchNo', 'OrderSSCC', 'Items'], note: 'Kolli. Ordre + (hvis plukk-koblet linje) batch/SSCC/vare.' },
  41: { kind: OVERRIDE, dbSources: [], note: 'Batch-pakkeetikett — tom kontekst. Kun PackageNo/VisiblePackageNo via override + firma/system.' },
  50: { kind: DB, dbSources: ['OrderSSCC', 'Orders', 'OrderLine', 'OrderBatchNo', 'Items', 'PList', 'PListSSCC', 'ContentLines (rader fra utskriften)'], note: 'SSCC/pall — bredest. SSCC kjeder gjennom ordre/linje/batch/vare; plukklistehodet via PList-ID. Innholdsetikett: varelinjer i ContentLines.' },
  51: { kind: DB, dbSources: ['OrderSSCC', 'Orders', 'OrderLine', 'OrderBatchNo', 'Items', 'PList', 'PListSSCC', 'ContentLines (rader fra utskriften)'], note: 'SSCC transport — som type 50.' },
  60: { kind: OVERRIDE, dbSources: [], note: 'Diverse/generisk etikett — tom kontekst. Kun FreeText via override + firma/system.' },
  70: { kind: OVERRIDE, dbSources: [], note: 'Sorteringskasse — tom kontekst. Kun StcBoxNo/StcBoxTypeID via override + firma/system.' },
  // 20 (ShippingLabel) og 30 (ConsignmentNote) fjernet 2026-07-15 — døde typer.
  // Frakt håndteres av TA-providerne (TaLabelPrinter) og logges i PrintLog som type 99.
  99: { kind: OVERRIDE, dbSources: [], note: 'Ekstern/TA — ikke designer-beriket på vanlig måte.' },
};

/**
 * Slår opp berikelsesinfo for en rapporttype. Ukjente verdier (utenfor enum-settet)
 * skal ikke forekomme — de behandles som override-only.
 */
export function getEnrichmentInfo(reportType: number | undefined): EnrichmentInfo {
  if (reportType === undefined || reportType === null) {
    return { kind: OVERRIDE, dbSources: [], note: 'Ukjent rapporttype.' };
  }
  if (REPORT_TYPE_ENRICHMENT[reportType]) {
    return REPORT_TYPE_ENRICHMENT[reportType];
  }
  return { kind: OVERRIDE, dbSources: [], note: `Type ${reportType} — ingen kjent DB-berikelse; kun override + firma/system.` };
}

export type PlaceholderFill = 'db' | 'system' | 'override';

/**
 * Avgjør om en plassholder-nøkkel fylles automatisk for en gitt rapporttype:
 *  - 'system'   → firma/systemverdi, fylles alltid
 *  - 'db'       → fylles fra database (kilde-tabell er i typens dbSources)
 *  - 'override' → fylles KUN hvis print-kallet sender verdien eksplisitt
 *
 * Håndterer både enkle nøkler ("ItemNo") og tabell-prefiksede ("Items.Description1").
 */
export function classifyPlaceholderKey(key: string, reportType: number | undefined): PlaceholderFill {
  if (SYSTEM_KEYS.has(key)) return 'system';

  const info = getEnrichmentInfo(reportType);
  const source = key.includes('.') ? key.split('.')[0] : KEY_SOURCE_TABLE[key];

  // SystemSetup-prefiksede tabellkolonner er alltid tilgjengelige
  if (source === 'SystemSetup') return 'system';

  if (source && info.dbSources.includes(source)) return 'db';
  return 'override';
}

/** Symbol brukt som prefiks i plassholder-nedtrekk. */
export const FILL_MARKER: Record<PlaceholderFill, string> = {
  db: '●',
  system: '●',
  override: '○',
};
